import React, { FC, useEffect } from 'react';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { useTranslations } from '../../../hooks/useTranslations';
import { Item } from '../../../../../__generated__/loma';
import { useSettings } from '../../../hooks/useSettings';
import { LayoutTree } from '../../features/DynamicLayout';
import Panel from '../../ui/Panel/Panel';
import PanelHeader from '../../ui/PanelHeader/PanelHeader';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import useOrderTracking from '../../../hooks/useOrderTracking';
import { useTheme } from '../../../hooks/useTheme';
import Carousel from '../../ui/Carousel/Carousel';
import ItemTile from './components/ItemTile';
import { useGlobalSettings } from '../../../hooks/useGlobalSettings';
import GiftTrackingMessage from './components/GiftTrackingMessage';

const { FeatureLayout, CategoryConditionCategory } = LayoutTree;
export { ItemTile };

export interface ItemsInShipmentProps {
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
  fixedPanelHeight?: string;
}

const ItemsInThisShipment: FC<ItemsInShipmentProps> = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight,
}) => {
  const {
    state: { category },
  } = usePageContext();
  const logEvent = useAnalyticsPost();
  const isLandscape = layout === FeatureLayout.LANDSCAPE;
  const slidesToScrollAndShow = !isLandscape ? 1 : 2;
  const isGiftExperience = category === CategoryConditionCategory.SHARING;

  const { itemsInThisShipment } = useSettings();

  const { showCarouselDots } = itemsInThisShipment || {
    showCarouselDots: true,
  };

  const {
    itemsInShipmentPanelTitle,
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3,
  } = useTranslations();

  const itemTextTemplates = {
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3,
  };

  const { panelcontainerNoneNone: panelContainerStyle } = useTheme();

  const { giftSharingEnabled } = useGlobalSettings();

  const { getCurrentShipment, orderTrackingData } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  useEffect(
    () => {
      logEvent({
        eventComponent: NarvarEventComponents.NTH_PACK_ITEMS_IN_THIS_SHIPMENT,
        eventFeature: NarvarEventComponents.NTH_PACK_ITEMS_IN_THIS_SHIPMENT,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
        feature: featureName,
        pageComponent: NarvarEventComponents.NTH_PACK_ITEMS_IN_THIS_SHIPMENT,
        trackLayout: layout,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { shipments = [] } = orderTrackingData?.orders[0] || {};

  if (
    !currentShipment ||
    (currentShipment.type === 'RETURN' &&
      currentShipment.status === 'JUSTSHIPPED' &&
      shipments.length > 1)
  )
    return null;

  const { items } = currentShipment;

  if (items.length === 0) return null;

  // group items in pairs so we can display 2 per carousel tile
  const itemChunks: [Item, Item?][] =
    items.length === slidesToScrollAndShow && slidesToScrollAndShow === 2
      ? [[items[0]], [items[1]]]
      : items.reduce(
          (allItemChunks: [Item, Item?][], _, index: number, arr) => {
            if (index % 2 === 0) {
              const [itemChunk0, itemChunk1] = arr.slice(index, index + 2);
              if (itemChunk0) {
                allItemChunks.push([itemChunk0, itemChunk1]);
              }
            }
            return allItemChunks;
          },
          [],
        );

  return (
    <Panel
      header={<PanelHeader>{itemsInShipmentPanelTitle}</PanelHeader>}
      sOuter={{
        boxSizing: 'border-box',
        ...panelContainerStyle,
        overflowX: 'hidden',
        height:
          fixedPanelHeight ||
          (panelContainerStyle && panelContainerStyle.height) ||
          '100%',
      }}
      sInner={{
        height: fixedPanelHeight ? 'auto' : '100%',
        maxHeight: fixedPanelHeight ? '100%' : 'auto',
      }}
    >
      {isGiftExperience && giftSharingEnabled ? (
        <GiftTrackingMessage
          featureName={featureName}
          isLandscape={isLandscape}
        />
      ) : (
        <Carousel
          itemsCount={itemChunks.length}
          slickProps={{
            dots: showCarouselDots || false,
            slidesToScroll: slidesToScrollAndShow,
            slidesToShow: slidesToScrollAndShow,
            infinite: false,
          }}
          carouselContainerStyles={{ margin: '1rem' }}
        >
          {(Array.isArray(itemChunks) ? itemChunks : []).map(
            ([itemChunk0, itemChunk1]) => (
              <div
                key={`${itemChunk0?.productSku ?? ''} ${
                  itemChunk1?.productSku ?? ''
                }`}
              >
                <ItemTile
                  itemInfo={itemChunk0}
                  itemTextTemplates={itemTextTemplates}
                  featureName={featureName}
                />
                {itemChunk1 && (
                  <ItemTile
                    itemInfo={itemChunk1}
                    itemTextTemplates={itemTextTemplates}
                    featureName={featureName}
                  />
                )}
              </div>
            ),
          )}
        </Carousel>
      )}
    </Panel>
  );
};

export default ItemsInThisShipment;
